import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextChain } from '@/api/types/stowed-element'
import { chainContaining } from '@/lib/text-linking/text-chain-mutator'
import { canvasMinForSingleShapePanel } from '@/lib/shapes/shape-panel-bounds'
import type { ClipboardShapeSnapshot } from './types/clipboard-shape-snapshot'

/**
 * Whether a locked object should block a **copy**. Locked normally blocks every clipboard action, but
 * copy is the exception: it is read-only and non-destructive, so a locked *linked* text frame stays
 * copyable and its content remains available for pasting (WMPCM-7012 AC3). Cut, delete, and freeze keep
 * their unconditional locked block — this carve-out is deliberately copy-only.
 *
 * Chain membership is keyed by NSD `uid`, not `id`, and covers Text-tool frames *and* shapes carrying
 * `shapeTextParagraphs` (WMPCM-7988) — hence one structural predicate rather than two near-duplicates
 * that could drift apart the way the paste-side uid strip did. An object with no uid has never been
 * linked, so it keeps plain locked behavior.
 *
 * Shared by the canvas gates (`copySelectionToClipboard`, `canUseClipboardEntry`, context menus) and
 * the menu-bar enablement in `use-home-menu-commands.ts`, so Copy never greys out in one surface while working in another.
 */
export function lockedObjectBlocksCopy(
  obj: { locked?: boolean; uid?: number },
  chains: TextChain[] | undefined
): boolean {
  if (obj.locked !== true) return false
  if (typeof obj.uid !== 'number' || obj.uid <= 0) return true

  return chainContaining(chains ?? [], String(obj.uid)) == null
}

/** Record panel min + origin offset so paste can land on the same panel coordinates. */
export function attachPastePanelAnchorToShapeSnapshot(shape: ShapeData): ClipboardShapeSnapshot {
  const { minX, minY } = canvasMinForSingleShapePanel(shape)

  return {
    ...shape,
    pastePanelAnchor: {
      panelMinX: minX,
      panelMinY: minY,
      originToPanelDx: minX - shape.x,
      originToPanelDy: minY - shape.y,
    },
  }
}

/** Top-left placement for `addShape(..., anchor: 'top-left')` preserving panel min (+ optional offset). */
export function resolveShapePastePlacement(
  snapshot: ClipboardShapeSnapshot,
  offset: number
): { x: number; y: number } {
  const anchor = snapshot.pastePanelAnchor

  if (!anchor) {
    return { x: snapshot.x + offset, y: snapshot.y + offset }
  }

  return {
    x: anchor.panelMinX + offset - anchor.originToPanelDx,
    y: anchor.panelMinY + offset - anchor.originToPanelDy,
  }
}
